/* eslint-disable max-len */
import { parseInt } from 'lodash';
import type { StoryMediaQuery, StoryModel } from 'types/story';
import { cutURLMethod, IMAGE_PARAMS, isGif, isImageEditableURL, isSvg } from 'utils/assets';

const IMGIX_PARAMS_PRESET = {
	/* 'fit="fillmax"' restricts the loadable image size to the maximum size of the original image. For instance,
	   if a request specifies a width of 1800 pixels but the original image width is 100 pixels,
	   the resulting image will not exceed the original width.
	 */
	DESKTOP: `${IMAGE_PARAMS.width}=1800&${IMAGE_PARAMS.quality}=80&${IMAGE_PARAMS.fit}=fillmax&${IMAGE_PARAMS.auto}=format,compress`,
	MOBILE: `${IMAGE_PARAMS.width}=750&${IMAGE_PARAMS.quality}=80&${IMAGE_PARAMS.fit}=fillmax&${IMAGE_PARAMS.auto}=format,compress`,
	TOOL_ICON: `${IMAGE_PARAMS.quality}=80&${IMAGE_PARAMS.width}=64`,
};

type BuildImageUrlCommonProps = {
	src: string;
	storycardsDomain: StoryModel['storycardsDomain'];
};

type BuildBackgroundImageUrl = BuildImageUrlCommonProps & { mq: StoryMediaQuery['config'] };

type BuildResizableImageUrl = BuildImageUrlCommonProps & { width: number };

type BuildImageUrlProps = BuildBackgroundImageUrl | BuildResizableImageUrl;

// configure image src with unsupported format
const handleUnsupportedFormats = (src: string): string => {
	try {
		const url = new URL(src);
		return `${url.origin}${url.pathname}`;
	} catch (e) {
		console.error(e);
		return src;
	}
};

// configure image src for background-image format. non-resizable. e.g. "Card" block
const handleBackgroundImageFormat = (props: BuildBackgroundImageUrl): string => {
	const hasParams = props.src.match(/\?/);
	const mobileSize = props.mq.mobile.maxWidth ?? 0;
	const params = window.innerWidth <= mobileSize ? IMGIX_PARAMS_PRESET.MOBILE : IMGIX_PARAMS_PRESET.DESKTOP;
	return `${props.src}${hasParams ? '&' : '?'}${params}`;
};

// configure resizable image src
const handleResizableImageUrl = (props: BuildResizableImageUrl): string => {
	try {
		const url = new URL(props.src);
		const oW = +(url.searchParams.get(IMAGE_PARAMS.oW) ?? '0');
		const width = Math.min(oW || 1920 * 2, Math.round(props.width * window.devicePixelRatio));
		if (width > 0) {
			url.searchParams.set(IMAGE_PARAMS.width, `${width}`);
			url.searchParams.set(IMAGE_PARAMS.quality, '80');
			url.searchParams.set(IMAGE_PARAMS.auto, 'format,compress');
			return url.href;
		}
		return props.src;
	} catch (e) {
		// return props.src;
		console.error(e);
		return props.src;
	}
};

const adjustImageUrlForPreload = (src: string): string => {
	try {
		const url = new URL(src);
		url.searchParams.set(IMAGE_PARAMS.width, '50');
		url.searchParams.set(IMAGE_PARAMS.quality, '5');
		url.searchParams.set(IMAGE_PARAMS.blur, '20');
		return url.href;
	} catch (e) {
		console.error(e);
		return src;
	}
};

export const adjustImageUrlByProvider = (src: string, storycardsDomain: StoryModel['storycardsDomain']) => {
	if (storycardsDomain?.provider === 'Cloudflare') {
		try {
			const url = new URL(src);
			const paramsMap = {
				w: url.searchParams.get(IMAGE_PARAMS.width),
				q: url.searchParams.get(IMAGE_PARAMS.quality),
				rect: url.searchParams.get(IMAGE_PARAMS.rect),
				blur: url.searchParams.get(IMAGE_PARAMS.blur),
				fit: url.searchParams.get(IMAGE_PARAMS.fit),
				optimization: url.searchParams.get(IMAGE_PARAMS.auto),
			};

			let trim: null | string = null;
			if (paramsMap.rect) {
				const [left, top, width, height] = paramsMap.rect.split(',');
				const n = (v: string) => Math.max(0, parseInt(v, 10));
				trim = `trim.left=${n(left)},trim.top=${n(top)},trim.width=${n(width)},trim.height=${n(height)}`;
			}

			const options = [
				trim,
				paramsMap.w ? `width=${paramsMap.w}` : null,
				paramsMap.q ? `quality=${paramsMap.q}` : null,
				paramsMap.blur ? `blur=${paramsMap.blur}` : null,
				paramsMap.fit ? `fit=scale-down` : null,
				paramsMap.optimization ? 'format=auto' : null,
			]
				.filter(Boolean)
				.join(',');

			url.pathname = `/cdn-cgi/image/${options}${url.pathname}`;

			url.searchParams.delete(IMAGE_PARAMS.width);
			url.searchParams.delete(IMAGE_PARAMS.quality);
			url.searchParams.delete(IMAGE_PARAMS.rect);
			url.searchParams.delete(IMAGE_PARAMS.blur);
			url.searchParams.delete(IMAGE_PARAMS.fit);
			url.searchParams.delete(IMAGE_PARAMS.auto);

			return url.href;
		} catch (e) {
			console.error(e);
		}
	}

	return src;
};

// TODO: consider to create new URL here and pass it to the utilities
export const buildImageUrl = (props: BuildImageUrlProps): { src: string; lowQualitySrc: string } => {
	let src = decodeURIComponent(cutURLMethod(props.src));
	const isNone = src === 'none';
	const isSvgFormat = isSvg(src);
	const isGifFormat = isGif(src);
	const isEditableURL = isImageEditableURL(src, props.storycardsDomain);
	const isAdjustable = isEditableURL && !isSvgFormat && !isGifFormat && !isNone;

	if (isEditableURL && (isSvgFormat || isGifFormat)) {
		src = handleUnsupportedFormats(src);
	} else if ('mq' in props && isEditableURL && !isNone && !isSvgFormat && !isGifFormat) {
		src = handleBackgroundImageFormat({ ...props, src });
	} else if ('width' in props) {
		src = handleResizableImageUrl({ ...props, src });
	}

	return {
		src: isAdjustable ? adjustImageUrlByProvider(src, props.storycardsDomain) : src,
		lowQualitySrc: isAdjustable
			? adjustImageUrlByProvider(adjustImageUrlForPreload(src), props.storycardsDomain)
			: src,
	};
};
